import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/utils'

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('bg-surface rounded shadow-card', className)}>{children}</div>
}

export function CardHeader({
  title,
  icon,
  actions,
  className,
}: {
  title: ReactNode
  icon?: string
  actions?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3 px-4 py-3.5 border-b border-border/70', className)}>
      <h4 className="flex items-center gap-2 text-base font-medium text-white">
        {icon && <i className={cn(icon, 'text-lg text-muted')} aria-hidden />}
        {title}
      </h4>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export function CardBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('p-4', className)}>{children}</div>
}

export type Tone = 'primary' | 'income' | 'expense' | 'info' | 'warning' | 'muted'

export const SOFT_TONE: Record<Tone, string> = {
  primary: 'bg-primary/10 text-primary',
  income: 'bg-income/10 text-income',
  expense: 'bg-expense/10 text-expense',
  info: 'bg-info/10 text-info',
  warning: 'bg-warning/10 text-warning',
  muted: 'bg-surface3 text-muted',
}

export function AvatarIcon({ icon, tone = 'primary', size = 'md', className }: { icon: string; tone?: Tone; size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const sizes = { sm: 'h-8 w-8 text-base', md: 'h-10 w-10 text-xl', lg: 'h-12 w-12 text-2xl' }
  return (
    <span className={cn('inline-flex flex-shrink-0 items-center justify-center rounded', sizes[size], SOFT_TONE[tone], className)}>
      <i className={icon} aria-hidden />
    </span>
  )
}

export function KpiCard({
  title,
  value,
  icon,
  tone = 'primary',
  change,
  link,
  footer,
}: {
  title: string
  value: string
  icon: string
  tone?: Tone
  change?: { value: string; positive: boolean }
  link?: { label: string; to: string }
  /** Conteúdo extra abaixo do valor (ex.: pendente e previsto). */
  footer?: ReactNode
}) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-center justify-between gap-2">
          <p className="text-13 font-medium uppercase text-muted truncate">{title}</p>
          {change && (
            <span className={cn('flex items-center text-13 font-medium', change.positive ? 'text-income' : 'text-expense')}>
              <i className={change.positive ? 'ri-arrow-right-up-line' : 'ri-arrow-right-down-line'} aria-hidden />
              {change.value}
            </span>
          )}
        </div>
        <div className="mt-4 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h4 className="text-2xl font-semibold text-white truncate">{value}</h4>
            {link && (
              <Link to={link.to} className="mt-2 inline-block text-13 text-muted underline underline-offset-2 hover:text-primary">
                {link.label}
              </Link>
            )}
          </div>
          <AvatarIcon icon={icon} tone={tone} size="lg" />
        </div>
        {footer}
      </CardBody>
    </Card>
  )
}

export function ProgressBar({ value, tone = 'primary', className }: { value: number; tone?: Tone; className?: string }) {
  const fill: Record<Tone, string> = {
    primary: 'bg-gradient-primary',
    income: 'bg-income',
    expense: 'bg-expense',
    info: 'bg-info',
    warning: 'bg-warning',
    muted: 'bg-muted',
  }
  const width = Math.max(0, Math.min(value, 100))
  return (
    <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-surface3', className)} role="progressbar" aria-valuenow={Math.round(width)} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn('h-full rounded-full transition-all duration-500', fill[tone])} style={{ width: `${width}%` }} />
    </div>
  )
}
